// Battle input: selection (click / drag-box / roster / control groups), orders (move,
// attack, capture, attack-move, stop, hold, formation-facing drag), stances, hero
// abilities (with ground targeting), and camera control (pan/zoom/edge-scroll). Clicks
// over HUD chrome are routed to the Hud region system.

import { dist, type Vec2 } from "../engine/vec";
import { GameLoop } from "../engine/loop";
import { applyAbility } from "../sim/combat";
import { enqueueStructure, enqueueTier, enqueueUnit, enqueueUpgrade, fortifyPoint } from "../sim/economy";
import type { Faction, Order, Squad } from "../sim/types";
import type { World } from "../sim/world";
import { ICamera } from "../render/camera";
import { Hud, RegionAction } from "../render/hud";

export interface Ui {
  playerFaction: Faction;
  selected: Set<number>;
  hoverWorld: Vec2 | null;
  hoverScreen: Vec2;
  selectionBoxScreen: { a: Vec2; b: Vec2 } | null;
  facingDrag: { fromScreen: Vec2; toScreen: Vec2 } | null;
  attackMoveArmed: boolean;
  abilityTargeting: string | null;
  message: string;
  messageTimer: number;
  animTime: number;
}

export function newUi(playerFaction: Faction): Ui {
  return {
    playerFaction,
    selected: new Set(),
    hoverWorld: null,
    hoverScreen: { x: 0, y: 0 },
    selectionBoxScreen: null,
    facingDrag: null,
    attackMoveArmed: false,
    abilityTargeting: null,
    message: "",
    messageTimer: 0,
    animTime: 0,
  };
}

const ORD = (o: Partial<Order>): Order => ({ type: "stop", pos: null, facing: null, targetSquad: -1, targetPoint: -1, ...o });

export class BattleInput {
  private canvas: HTMLCanvasElement;
  private cam: ICamera;
  private hud: Hud;
  private loop: GameLoop;
  ui: Ui;
  getWorld: () => World;

  private keys = new Set<string>();
  private controlGroups: Record<number, number[]> = {};
  private lmbDown = false;
  private rmbDown = false;
  private rmbStart: Vec2 = { x: 0, y: 0 };
  private midDown = false;
  private mouseInside = false;
  private lastMid: Vec2 = { x: 0, y: 0 };
  private lastClickTime = 0;
  private lastClickSquad = -1;
  private lastGroupKey = "";
  private lastGroupTime = 0;
  private bound: Array<[string, EventListener]> = [];

  constructor(canvas: HTMLCanvasElement, cam: ICamera, hud: Hud, loop: GameLoop, ui: Ui, getWorld: () => World) {
    this.canvas = canvas;
    this.cam = cam;
    this.hud = hud;
    this.loop = loop;
    this.ui = ui;
    this.getWorld = getWorld;
    this.attach();
  }

  private on<K extends keyof WindowEventMap>(target: EventTarget, ev: K, fn: (e: WindowEventMap[K]) => void): void {
    const listener = fn as EventListener;
    target.addEventListener(ev, listener);
    this.bound.push([ev, listener]);
    (listener as any)._target = target;
  }

  detach(): void {
    for (const [ev, l] of this.bound) ((l as any)._target as EventTarget).removeEventListener(ev, l);
    this.bound = [];
  }

  private attach(): void {
    this.on(this.canvas, "mousedown", (e) => this.onMouseDown(e));
    this.on(window, "mouseup", (e) => this.onMouseUp(e));
    this.on(window, "mousemove", (e) => this.onMouseMove(e));
    this.on(this.canvas, "mouseenter", () => (this.mouseInside = true));
    this.on(this.canvas, "mouseleave", () => (this.mouseInside = false));
    this.on(this.canvas, "contextmenu", (e) => e.preventDefault());
    this.on(window, "keydown", (e) => this.onKeyDown(e));
    this.on(window, "keyup", (e) => this.keys.delete(e.key.toLowerCase()));
    // wheel needs passive:false so we can preventDefault the page zoom/scroll
    const wheel = (e: WheelEvent) => this.onWheel(e);
    this.canvas.addEventListener("wheel", wheel as EventListener, { passive: false });
    this.bound.push(["wheel", wheel as EventListener]);
    (wheel as any)._target = this.canvas;
  }

  private mouse(e: MouseEvent): Vec2 {
    const r = this.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private playerSquads(): Squad[] {
    return this.getWorld().aliveSquads(this.ui.playerFaction);
  }

  private squadAtScreen(pt: Vec2, faction?: Faction): Squad | null {
    const w = this.getWorld();
    let best: Squad | null = null;
    let bd = 22;
    for (const sq of w.squads) {
      if (sq.dead) continue;
      if (faction && sq.faction !== faction) continue;
      for (const m of sq.models) {
        if (!m.alive) continue;
        const s = this.cam.worldToScreen(m.pos);
        const d = dist(s, pt);
        if (d < bd) {
          bd = d;
          best = sq;
        }
      }
    }
    return best;
  }

  // ---- mouse ---------------------------------------------------------------

  private onMouseDown(e: MouseEvent): void {
    const pt = this.mouse(e);
    if (e.button === 1) {
      this.midDown = true;
      this.lastMid = pt;
      e.preventDefault();
      return;
    }
    // HUD first
    const region = this.hud.regionAt(pt.x, pt.y);
    if (region) {
      if (e.button === 0) this.handleRegion(region, e);
      return;
    }
    if (this.hud.overChrome(pt.x, pt.y, this.cam.vw, this.cam.vh)) return;

    if (e.button === 0) {
      // ability ground-target?
      if (this.ui.abilityTargeting) {
        this.castAbilityAt(this.cam.screenToWorld(pt));
        return;
      }
      // attack-move armed → issue at click
      if (this.ui.attackMoveArmed) {
        this.issueGroundOrder(this.cam.screenToWorld(pt), true);
        this.ui.attackMoveArmed = false;
        return;
      }
      this.lmbDown = true;
      this.ui.selectionBoxScreen = { a: { ...pt }, b: { ...pt } };
    } else if (e.button === 2) {
      this.rmbDown = true;
      this.rmbStart = pt;
    }
  }

  private onMouseMove(e: MouseEvent): void {
    const pt = this.mouse(e);
    this.mouseInside = pt.x >= 0 && pt.y >= 0 && pt.x <= this.cam.vw && pt.y <= this.cam.vh;
    this.ui.hoverScreen = pt;
    this.ui.hoverWorld = this.cam.screenToWorld(pt);
    if (this.midDown) {
      this.cam.panScreen(this.lastMid.x - pt.x, this.lastMid.y - pt.y);
      this.lastMid = pt;
    }
    if (this.lmbDown && this.ui.selectionBoxScreen) this.ui.selectionBoxScreen.b = { ...pt };
    if (this.rmbDown && dist(pt, this.rmbStart) > 12) {
      this.ui.facingDrag = { fromScreen: { ...this.rmbStart }, toScreen: { ...pt } };
    }
  }

  private onMouseUp(e: MouseEvent): void {
    const pt = this.mouse(e);
    if (e.button === 1) {
      this.midDown = false;
      return;
    }
    if (e.button === 0 && this.lmbDown) {
      this.lmbDown = false;
      const box = this.ui.selectionBoxScreen;
      this.ui.selectionBoxScreen = null;
      if (!box) return;
      if (dist(box.a, box.b) < 6) this.clickSelect(pt, e.shiftKey);
      else this.boxSelect(box, e.shiftKey);
    } else if (e.button === 2 && this.rmbDown) {
      this.rmbDown = false;
      const world = this.cam.screenToWorld(pt);
      if (this.ui.facingDrag) {
        const from = this.cam.screenToWorld(this.ui.facingDrag.fromScreen);
        const facing = Math.atan2(world.y - from.y, world.x - from.x);
        this.issueGroundOrder(from, this.keys.has("a"), facing);
        this.ui.facingDrag = null;
      } else {
        this.rightClickOrder(pt);
      }
    }
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    this.cam.zoomAt(this.mouse(e), e.deltaY < 0 ? 1.12 : 0.89);
  }

  // ---- selection -----------------------------------------------------------

  private clickSelect(pt: Vec2, additive: boolean): void {
    const sq = this.squadAtScreen(pt);
    if (!sq) {
      if (!additive) this.clearSelection();
      return;
    }
    if (sq.faction !== this.ui.playerFaction) {
      // selecting an enemy just shows it (single)
      this.setSelection([sq.id]);
      return;
    }
    if (additive) {
      if (this.ui.selected.has(sq.id)) this.ui.selected.delete(sq.id);
      else this.ui.selected.add(sq.id);
    } else {
      this.setSelection([sq.id]);
    }
    this.syncSelectedFlags();
  }

  private boxSelect(box: { a: Vec2; b: Vec2 }, additive: boolean): void {
    const x0 = Math.min(box.a.x, box.b.x);
    const x1 = Math.max(box.a.x, box.b.x);
    const y0 = Math.min(box.a.y, box.b.y);
    const y1 = Math.max(box.a.y, box.b.y);
    if (!additive) this.ui.selected.clear();
    for (const sq of this.playerSquads()) {
      for (const m of sq.models) {
        if (!m.alive) continue;
        const s = this.cam.worldToScreen(m.pos);
        if (s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1) {
          this.ui.selected.add(sq.id);
          break;
        }
      }
    }
    this.syncSelectedFlags();
  }

  private setSelection(ids: number[]): void {
    this.ui.selected = new Set(ids);
    this.syncSelectedFlags();
  }

  private clearSelection(): void {
    this.ui.selected.clear();
    this.syncSelectedFlags();
  }

  private syncSelectedFlags(): void {
    const w = this.getWorld();
    for (const sq of w.squads) sq.selected = this.ui.selected.has(sq.id);
  }

  // ---- orders --------------------------------------------------------------

  private selectedPlayerSquads(): Squad[] {
    const w = this.getWorld();
    return [...this.ui.selected]
      .map((id) => w.squads.find((s) => s.id === id && !s.dead))
      .filter((s): s is Squad => !!s && s.faction === this.ui.playerFaction);
  }

  private rightClickOrder(pt: Vec2): void {
    const enemy = this.squadAtScreen(pt, this.ui.playerFaction === "legion" ? "kindled" : "legion");
    if (enemy) {
      for (const sq of this.selectedPlayerSquads()) sq.order = ORD({ type: "attack", targetSquad: enemy.id });
      this.flash("Attack!");
      return;
    }
    this.issueGroundOrder(this.cam.screenToWorld(pt), this.keys.has("a"));
  }

  private issueGroundOrder(world: Vec2, attackMove: boolean, facing?: number): void {
    const w = this.getWorld();
    // capture if near a point
    let pointIdx = -1;
    let bd = 90;
    w.points.forEach((p, i) => {
      const d = dist(p.pos, world);
      if (d < bd) {
        bd = d;
        pointIdx = i;
      }
    });
    const squads = this.selectedPlayerSquads();
    if (squads.length === 0) return;
    // spread destinations a little for multiple squads
    squads.forEach((sq, i) => {
      const off = squads.length > 1 ? spread(i, squads.length, 60) : { x: 0, y: 0 };
      const dest = { x: world.x + off.x, y: world.y + off.y };
      if (pointIdx >= 0 && w.points[pointIdx].owner !== this.ui.playerFaction) {
        sq.order = ORD({ type: "capture", pos: { ...w.points[pointIdx].pos }, targetPoint: pointIdx, facing: facing ?? null });
      } else {
        sq.order = ORD({ type: attackMove ? "attackmove" : "move", pos: dest, facing: facing ?? null });
      }
    });
    this.flash(pointIdx >= 0 ? "Capture" : attackMove ? "Attack-move" : "Move");
  }

  // ---- hero abilities ------------------------------------------------------

  private castAbilityAt(world: Vec2): void {
    const abilityId = this.ui.abilityTargeting;
    this.ui.abilityTargeting = null;
    if (!abilityId) return;
    const hero = this.getWorld().armies[this.ui.playerFaction].hero;
    if (!hero || hero.dead) return;
    const ab = hero.def.abilities?.find((a) => a.id === abilityId);
    if (ab) applyAbility(this.getWorld(), hero, ab, world);
  }

  private triggerAbility(index: number): void {
    const w = this.getWorld();
    // hero must be selected or just use army hero
    const hero = w.armies[this.ui.playerFaction].hero;
    if (!hero || hero.dead || !hero.def.abilities) return;
    const ab = hero.def.abilities[index];
    if (!ab) return;
    if ((hero.abilityCd[ab.id] ?? 0) > 0) {
      this.flash(`${ab.name} on cooldown`);
      return;
    }
    if (ab.kind === "artillery" || ab.kind === "immolate") {
      this.ui.abilityTargeting = ab.id;
      this.flash(`${ab.name}: click target`);
    } else {
      applyAbility(w, hero, ab, null);
      this.flash(ab.name);
    }
  }

  // ---- HUD regions ---------------------------------------------------------

  private handleRegion(r: { action: RegionAction; enabled: boolean }, e: MouseEvent): void {
    const w = this.getWorld();
    const a = r.action;
    switch (a.kind) {
      case "roster": {
        const now = performance.now();
        if (this.lastClickSquad === a.squadId && now - this.lastClickTime < 300) {
          const sq = w.squads.find((s) => s.id === a.squadId);
          if (sq) this.cam.centerOn(sq.center);
        }
        this.lastClickSquad = a.squadId;
        this.lastClickTime = now;
        if (e.shiftKey) {
          if (this.ui.selected.has(a.squadId)) this.ui.selected.delete(a.squadId);
          else this.ui.selected.add(a.squadId);
        } else this.setSelection([a.squadId]);
        this.syncSelectedFlags();
        break;
      }
      case "build_unit":
        if (!enqueueUnit(w, this.ui.playerFaction, a.unitId)) this.flash("Can't build (cost/cap/tier)");
        break;
      case "build_struct":
        if (!enqueueStructure(w, this.ui.playerFaction, a.structKind)) this.flash("Can't build");
        break;
      case "build_tier":
        if (!enqueueTier(w, this.ui.playerFaction)) this.flash("Can't advance");
        break;
      case "build_upgrade":
        if (!enqueueUpgrade(w, this.ui.playerFaction, a.squadId, a.upgradeId)) this.flash("Can't upgrade");
        break;
      case "fortify":
        {
          const sq = w.squads.find((s) => s.id === a.squadId);
          const idx = sq ? w.points.findIndex((p) => p.owner === this.ui.playerFaction && !p.hasOutpost && dist(p.pos, sq.center) < p.captureRadius) : -1;
          if (idx < 0 || !fortifyPoint(w, this.ui.playerFaction, idx)) this.flash("Can't fortify");
        }
        break;
      case "stance":
        for (const sq of this.selectedPlayerSquads()) sq.stance = a.value;
        break;
      case "ability": {
        const hero = w.armies[this.ui.playerFaction].hero;
        const idx = hero?.def.abilities?.findIndex((x) => x.id === a.abilityId) ?? -1;
        if (idx >= 0) this.triggerAbility(idx);
        break;
      }
      case "cancel_queue":
        w.armies[this.ui.playerFaction].queue.splice(a.index, 1);
        break;
    }
  }

  // ---- keyboard ------------------------------------------------------------

  private onKeyDown(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    this.keys.add(k);
    if (k.startsWith("arrow") || k === " ") e.preventDefault();
    if (k >= "1" && k <= "9") {
      this.handleGroupKey(k, e.ctrlKey || e.metaKey);
      return;
    }
    switch (k) {
      case "a":
        if (this.selectedPlayerSquads().length) {
          this.ui.attackMoveArmed = true;
          this.flash("Attack-move: click");
        }
        break;
      case "s":
        for (const sq of this.selectedPlayerSquads()) sq.order = ORD({ type: "stop", pos: { ...sq.center } });
        break;
      case "h":
        for (const sq of this.selectedPlayerSquads()) sq.order = ORD({ type: "hold", pos: { ...sq.center }, facing: sq.facing });
        break;
      case "q":
        this.triggerAbility(0);
        break;
      case "w":
        this.triggerAbility(1);
        break;
      case "e":
        this.triggerAbility(2);
        break;
      case "escape":
        this.ui.abilityTargeting = null;
        this.ui.attackMoveArmed = false;
        this.clearSelection();
        break;
      case " ":
        this.loop.speed = this.loop.speed > 0 ? 0 : 1;
        this.flash(this.loop.speed === 0 ? "Paused" : "Resumed");
        e.preventDefault();
        break;
      case "[":
        this.loop.speed = Math.max(0.25, this.loop.speed - 0.25);
        this.flash(`Speed ${this.loop.speed}x`);
        break;
      case "]":
        this.loop.speed = Math.min(4, this.loop.speed + 0.25);
        this.flash(`Speed ${this.loop.speed}x`);
        break;
    }
  }

  private handleGroupKey(k: string, assign: boolean): void {
    const n = parseInt(k, 10);
    if (assign) {
      this.controlGroups[n] = [...this.ui.selected];
      this.flash(`Group ${n} set`);
    } else {
      const ids = (this.controlGroups[n] ?? []).filter((id) => this.getWorld().squads.some((s) => s.id === id && !s.dead));
      if (ids.length) {
        this.setSelection(ids);
        const now = performance.now();
        if (this.lastGroupKey === k && now - this.lastGroupTime < 350) {
          const w = this.getWorld();
          const first = w.squads.find((s) => s.id === ids[0]);
          if (first) this.cam.centerOn(first.center);
        }
        this.lastGroupKey = k;
        this.lastGroupTime = now;
      }
    }
  }

  // ---- per-frame update (camera keys / edge scroll / timers) ----------------

  update(dt: number): void {
    const speed = 620 / this.cam.zoom;
    let dx = 0;
    let dy = 0;
    // Camera pan is arrow-keys only (letter keys are reserved for orders/abilities).
    if (this.keys.has("arrowleft")) dx -= 1;
    if (this.keys.has("arrowright")) dx += 1;
    if (this.keys.has("arrowup")) dy -= 1;
    if (this.keys.has("arrowdown")) dy += 1;
    // edge scroll (only when the pointer is actually inside the viewport)
    if (this.mouseInside) {
      const m = this.ui.hoverScreen;
      const edge = 18;
      if (m.x < edge) dx -= 1;
      if (m.x > this.cam.vw - edge) dx += 1;
      if (m.y < edge) dy -= 1;
      if (m.y > this.cam.vh - edge) dy += 1;
    }
    if (dx || dy) {
      this.cam.x += dx * speed * dt;
      this.cam.y += dy * speed * dt;
    }
    this.cam.clampToMap(this.getWorld().map.width, this.getWorld().map.height);
    if (this.ui.messageTimer > 0) {
      this.ui.messageTimer -= dt;
      if (this.ui.messageTimer <= 0) this.ui.message = "";
    }
    // sync facing drag world coords for renderer
  }

  flash(msg: string): void {
    this.ui.message = msg;
    this.ui.messageTimer = 2;
  }
}

function spread(i: number, n: number, gap: number): Vec2 {
  const cols = Math.ceil(Math.sqrt(n));
  const r = Math.floor(i / cols);
  const c = i % cols;
  return { x: (c - (cols - 1) / 2) * gap, y: (r - (cols - 1) / 2) * gap };
}
